import PropTypes from "prop-types"
import React from "react"

import Badge from "src/components/Badge"
import Tooltip from "src/components/Tooltip"

import { snakecaseToCamelKeys } from "src/utils/object_helpers"

const TEAL = ["Settled"]
const RED = ["Failed", "Disputed", "Overdue"]
const YELLOW = ["Processing", "Retrying"]
const GRAY = ["Scheduled"]

const getPaymentStatusBadgeColor = (status) => {
  if (TEAL.includes(status)) return "teal"
  if (RED.includes(status)) return "red"
  if (YELLOW.includes(status)) return "yellow"
  if (GRAY.includes(status)) return "gray"

  return "gray"
}

// Returns the normalized label and Badge color for a given displayStatus string.
// "Past Due" is normalized to "Overdue" (label and red color) to match legacy behavior.
export const getPaymentStatusBadgeProps = (displayStatus) => {
  const label = displayStatus === "Past Due" ? "Overdue" : displayStatus
  const color = getPaymentStatusBadgeColor(label)
  return { label, color }
}

const getPaymentStatusTooltip = (payment) => {
  const {
    settledAtLocalTime,
    displayStatus,
    autoSettleLastError,
    nextAutoSettleTime,
  } = payment

  switch (displayStatus) {
    case "Scheduled":
      return `Scheduled to settle on ${nextAutoSettleTime}`
    case "Settled":
      return settledAtLocalTime ? `Settled on ${settledAtLocalTime}` : null
    case "Retrying":
      return `${
        autoSettleLastError
          ? `Last autopay attempt was unsuccessful: ${autoSettleLastError}`
          : "Last autopay attempt was unsuccessful."
      } Retrying again at ${nextAutoSettleTime}.`
    default:
      return null
  }
}

const PaymentStatusBadge = ({ payment }) => {
  const normalizedPayment = snakecaseToCamelKeys(payment)
  const { displayStatus } = normalizedPayment
  const { label: finalDisplayStatus, color: paymentStatusBadgeColor } =
    getPaymentStatusBadgeProps(displayStatus)
  const paymentStatusTooltip = getPaymentStatusTooltip(normalizedPayment)

  if (paymentStatusTooltip) {
    return (
      <Tooltip text={paymentStatusTooltip} placement="top" variant="dark">
        <Badge color={paymentStatusBadgeColor} text={finalDisplayStatus} />
      </Tooltip>
    )
  } else {
    return <Badge color={paymentStatusBadgeColor} text={finalDisplayStatus} />
  }
}

PaymentStatusBadge.propTypes = {
  payment: PropTypes.shape({
    due_date: PropTypes.string,
    settled_at_local_time: PropTypes.string,
    display_status: PropTypes.string,
    displayStatus: PropTypes.string,
    auto_settle_last_error: PropTypes.string,
    next_auto_settle_time: PropTypes.string,
    settledAtLocalTime: PropTypes.string,
    autoSettleLastError: PropTypes.string,
    nextAutoSettleTime: PropTypes.string,
  }),
}

export default PaymentStatusBadge
